/* eslint-disable import/no-relative-packages */
/* eslint-disable no-param-reassign */
/* eslint-disable no-new */
/* eslint-disable import/prefer-default-export */
import * as d3 from 'd3'
import {
    getSixFootNormalized,
    topZone,
    bottomZone,
    plateWidthFt,
} from 'bam-node-baseball-savant/constants/zone.ts'
import {
    AbsChallengeDrawerRow,
    ServerParams,
} from './swing-path/types/globals.ts'

const { guff } = require('bam-node-baseball-savant/utils/guff')

declare const serverParams: ServerParams
declare const isProd: boolean

const FAIL_COLOR = '#000'
const SUCCESS_COLOR = '#399098'

const PLATE_WIDTH_INCHES = plateWidthFt * 12
const BALL_WIDTH_INCHES = 2.9
const BALL_STROKE_WIDTH = 1

type Mode = 'for' | 'against' | 'forcatchers' | 'againstcatchers'

let currMode: Mode
let isStrikeoutKey: keyof AbsChallengeDrawerRow
let isWalkKey: keyof AbsChallengeDrawerRow

function setStrikeoutAndWalkKeys() {
    isStrikeoutKey = `is_strikeout_overturn${
        currMode.includes('against') ? '_against' : ''
    }` as keyof AbsChallengeDrawerRow
    isWalkKey = `is_walk_overturn${
        currMode.includes('against') ? '_against' : ''
    }` as keyof AbsChallengeDrawerRow
}

export function plotPitches(
    svgId: string,
    uniqueId: string,
    vizData: AbsChallengeDrawerRow[],
    opts: {
        isTwoStrikes?: boolean
        isThreeBalls?: boolean
        isFlip?: boolean
    },
    buttonType?: Mode
) {
    if (!vizData) {
        return
    }

    if (buttonType) {
        currMode = buttonType
        setStrikeoutAndWalkKeys()
    }

    const zoneWidth = 113.2075471698
    const zoneHeight = 126.526082131
    const chart = d3.select(`#zone_${uniqueId}`)

    chart.select('.zone-wrapper').remove()

    const zoneWrapper = chart
        .append('g')
        .attr('transform', `translate(${zoneWidth / 2},${zoneHeight / 2})`)
        .attr('class', 'zone-wrapper')

    const xScaleNew = d3
        .scaleLinear()
        .domain([0, PLATE_WIDTH_INCHES / 24])
        .range([0, zoneWidth / 2])

    const yScaleNew = d3
        .scaleLinear()
        .domain([bottomZone, topZone])
        .range([zoneHeight / 2, zoneHeight / -2])

    const inchesToPx = d3
        .scaleLinear()
        .domain([0, PLATE_WIDTH_INCHES])
        .range([0, zoneWidth])

    zoneWrapper
        .selectAll<SVGAElement, AbsChallengeDrawerRow>('a')
        .data<AbsChallengeDrawerRow>(
            vizData
                .filter((d) => {
                    if (opts?.isTwoStrikes) {
                        return d.pre_strike_count === 2
                    }
                    return true
                })
                .filter((d) => {
                    if (opts?.isThreeBalls) {
                        return d.pre_ball_count === 3
                    }
                    return true
                })
                .filter((d) => {
                    if (opts?.isFlip) {
                        return d[isStrikeoutKey] || d[isWalkKey]
                    }
                    return true
                }),
            (d: AbsChallengeDrawerRow) => d.play_id
        )
        .join((enter) => {
            const linkWrap = enter
                .append('a')
                .attr('target', '_blank')
                .attr('class', `strike-zone-link-${uniqueId}`)

            if (isProd && serverParams.level === 'mlb') {
                linkWrap.attr(
                    'href',
                    (d) => `/sporty-videos?playId=${d.play_id}`
                )
            } else if (!isProd) {
                linkWrap.attr(
                    'href',
                    (d) =>
                        `https://research.mlb.com/games/${d.game_pk}/plays/${d.play_id}`
                )
            }

            linkWrap
                .append('circle')
                .attr('cx', (d) => {
                    const { x } = getSixFootNormalized(
                        Number(d.plateX),
                        Number(d.plateZ),
                        d.strikeZoneTop,
                        d.strikeZoneBottom,
                        d.widthinches
                    )
                    return xScaleNew(x)
                })
                .attr('cy', (d) => {
                    const { z } = getSixFootNormalized(
                        Number(d.plateX),
                        Number(d.plateZ),
                        d.strikeZoneTop,
                        d.strikeZoneBottom,
                        d.widthinches
                    )
                    return yScaleNew(z)
                })
                .attr(
                    'r',
                    inchesToPx(BALL_WIDTH_INCHES) / 2 - BALL_STROKE_WIDTH
                )
                .attr('class', `strike-zone-dot-${uniqueId}`)
                .attr('id', (d) => `strike-zone-dot-${d.play_id}`)
                .attr('stroke', 'black')
                .attr('stroke-width', BALL_STROKE_WIDTH)
                .attr('fill', (d) => {
                    if (d.is_challengeABS_overturned) {
                        return SUCCESS_COLOR
                    }
                    return FAIL_COLOR
                })
                .attr('fill-opacity', 0.5)
                .on('mouseover', (e, d) => {
                    d3.selectAll(`.strike-zone-dot-${uniqueId}`).attr(
                        'opacity',
                        0.2
                    )
                    d3.select(`#${e.target.id}`)
                        .attr('opacity', 1)
                        .attr('fill-opacity', 1)
                        .raise()
                    displayInfoBox(uniqueId, d.play_id, d)

                    const tableRow = document.getElementById(
                        `challenge_table_${uniqueId}-tr_${d.id}_${d.play_id}`
                    )

                    if (tableRow) {
                        tableRow.style.outline = `3px solid ${
                            d.is_challengeABS_overturned
                                ? SUCCESS_COLOR
                                : FAIL_COLOR
                        }`
                        tableRow.style.outlineOffset = '-3px'
                    }
                })
                .on('mouseout', (e, d) => {
                    d3.selectAll(`.strike-zone-dot-${uniqueId}`)
                        .attr('opacity', 1)
                        .attr('fill-opacity', 0.5)
                    d3.selectAll(`.tooltip-${d.play_id}`).remove()

                    const tableRow = document.getElementById(
                        `challenge_table_${uniqueId}-tr_${d.id}_${d.play_id}`
                    )
                    if (tableRow) {
                        tableRow.style.outline = ''
                        tableRow.style.outlineOffset = ''
                    }
                })
            return linkWrap
        })
}

function removeTooltips() {
    d3.selectAll('.tooltip-zone').remove()
}

export function handleTableHover(
    playId: string,
    playerId: string,
    uniqueId: string
) {
    const thisSVG = d3.select(`#all_viz_${uniqueId}_svg`)
    const thisDot = d3.select<SVGCircleElement, AbsChallengeDrawerRow>(
        `#strike-zone-dot-${playId}`
    )
    const datum = thisDot.datum()

    if (!thisDot.empty() && datum) {
        thisSVG.selectAll(`.strike-zone-dot-${uniqueId}`).attr('opacity', 0.2)
        thisDot.attr('opacity', 1).attr('fill-opacity', 1).raise()
        displayInfoBox(uniqueId, playId, datum)
    }
}

export function handleTableLeave(uniqueId: string) {
    d3.selectAll(`.strike-zone-dot-${uniqueId}`)
        .attr('opacity', 1)
        .attr('fill-opacity', 0.5)

    removeTooltips()
}

function getTooltipSummaryText(d: AbsChallengeDrawerRow) {
    let returnStr = `${
        d.is_challengeABS_overturned === 1 ? 'OVERTURNED' : 'CONFIRMED'
    }`

    if (d[isStrikeoutKey] && d[isWalkKey]) {
        returnStr += ' (K & BB FLIP)'
    } else if (d[isStrikeoutKey]) {
        returnStr += ' (K FLIP)'
    } else if (d[isWalkKey]) {
        returnStr += ' (BB FLIP)'
    }

    return returnStr
}

function getInningEnding(num: number) {
    if (num % 10 === 1 && num !== 11) {
        return 'st'
    }

    if (num % 10 === 2 && num !== 12) {
        return 'nd'
    }

    if (num % 10 === 3 && num !== 13) {
        return 'rd'
    }

    return 'th'
}

function getGameSitText(d: AbsChallengeDrawerRow) {
    const type = serverParams.challengeType ?? 'batter'
    const isDefense = ['catcher', 'catching-team'].includes(type)

    const firstTeam = isDefense ? 'fld_team_abbr' : 'bat_team_abbr'
    const firstScore = isDefense ? 'fld_score' : 'bat_score'
    const secondTeam = isDefense ? 'bat_team_abbr' : 'fld_team_abbr'
    const secondScore = isDefense ? 'bat_score' : 'fld_score'

    const scoreStr = `${d[firstTeam]} ${d[firstScore]}, ${d[secondTeam]} ${d[secondScore]}`
    const innStr = `${d.event_inning}${getInningEnding(Number(d.event_inning))}`

    return `${scoreStr} (${innStr})`
}

function displayInfoBox(
    uniqueId: string,
    playId: string,
    d: AbsChallengeDrawerRow
) {
    const chart = d3.select(`#zone_chart_${uniqueId}`)
    // const elemX = Number(d3.select(`#strike-zone-dot-${playId}`).attr('cx'))
    // const elemY = Number(d3.select(`#strike-zone-dot-${playId}`).attr('cy'))

    const TOOLTIP_WIDTH_WIDE = 170
    const TOOLTIP_WIDTH_MEDIUM = 145
    const TOOLTIP_WIDTH = 120
    const TOOLTIP_HEIGHT = 78
    // const TOOLTIP_BUFFER = 20

    let widthToUse = TOOLTIP_WIDTH
    if (isBothFlip(d)) {
        widthToUse = TOOLTIP_WIDTH_WIDE
    } else if (isFlip(d)) {
        widthToUse = TOOLTIP_WIDTH_MEDIUM
    }

    // const isBottomHalf = d.api_relative_plate_z < 0
    // const isLeftHalf = d.api_relative_plate_x < 0

    // const tooltipX = isLeftHalf ? elemX : elemX - widthToUse
    const xOffset = 5

    // const tooltipY = isBottomHalf
    //     ? elemY - TOOLTIP_BUFFER - TOOLTIP_HEIGHT
    //     : elemY + TOOLTIP_BUFFER
    const yOffset = 5
    const yLineHeight = 14

    const tooltipX = -widthToUse / 2
    const tooltipY = 95

    chart
        .append('rect')
        .attr('x', tooltipX)
        .attr('y', tooltipY)
        .attr('width', widthToUse)
        .attr('height', TOOLTIP_HEIGHT)
        .attr('rx', 2)
        .attr('stroke', 'black')
        .attr('stroke-width', 2)
        .attr('fill', 'white')
        .attr('class', `tooltip-zone tooltip-${playId}`)

    chart
        .append('text')
        .text(getTooltipSummaryText(d))
        .attr('x', tooltipX + xOffset)
        .attr('y', tooltipY + yOffset)
        .attr(
            'fill',
            d.is_challengeABS_overturned === 1 ? SUCCESS_COLOR : FAIL_COLOR
        )
        .style('font-weight', 'bold')
        .attr('class', `tooltip-zone tooltip-${playId} tooltip-text`)

    chart
        .append('text')
        .text(`${d.game_date.split('T')[0]}`)
        .attr('x', tooltipX + xOffset)
        .attr('y', tooltipY + yOffset + yLineHeight)
        .attr('class', `tooltip-zone tooltip-${playId} tooltip-text`)

    chart
        .append('text')
        .text(getGameSitText(d))
        .attr('x', tooltipX + xOffset)
        .attr('y', tooltipY + yOffset + 2 * yLineHeight)
        .attr('class', `tooltip-zone tooltip-${playId} tooltip-text`)

    chart
        .append('text')
        .text(`Chal. Prob: ${guff(d.sz_challenge_prob, 'abs_chal_percent')}`)
        .attr('x', tooltipX + xOffset)
        .attr('y', tooltipY + yOffset + 3 * yLineHeight)
        .attr('class', `tooltip-zone tooltip-${playId} tooltip-text`)

    chart
        .append('text')
        .text(`Count: ${d.pre_ball_count}-${d.pre_strike_count}`)
        .attr('x', tooltipX + xOffset)
        .attr('y', tooltipY + yOffset + 4 * yLineHeight)
        .attr('class', `tooltip-zone tooltip-${playId} tooltip-text`)
}

function isFlip(d: AbsChallengeDrawerRow) {
    return Number(d[isStrikeoutKey]) + Number(d[isWalkKey]) > 0
}

function isBothFlip(d: AbsChallengeDrawerRow) {
    return Number(d[isStrikeoutKey]) + Number(d[isWalkKey]) === 2
}
